import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/utils';
import type { ExecutionChatMessageInteraction } from '@shared/types/execution-chat';
import type { HITLFormField } from '@shared/types/hitl';

interface ExecutionChatInlineFormProps {
  interaction: ExecutionChatMessageInteraction;
  disabled: boolean;
  onSubmit: (answer: unknown) => Promise<void>;
  onCancel: () => Promise<void>;
}

const defaultLabelFromKey = (key: string): string => (
  key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (char) => char.toUpperCase())
);

export const ExecutionChatInlineForm = ({
  interaction,
  disabled,
  onSubmit,
  onCancel,
}: ExecutionChatInlineFormProps) => {
  const hitl = interaction.hitl;
  const [formData, setFormData] = useState<Record<string, unknown>>(hitl.formData ?? {});
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);

  useEffect(() => {
    setFormData(hitl.formData ?? {});
    setIsSubmittingLocal(false);
  }, [hitl.formData, hitl.stepId, hitl.questionId]);

  const schema = useMemo(() => {
    if (hitl.formSchema && Object.keys(hitl.formSchema).length > 0) {
      return hitl.formSchema;
    }
    const inferred: Record<string, HITLFormField> = {};
    for (const key of Object.keys(formData)) {
      inferred[key] = {
        type: 'text',
        label: defaultLabelFromKey(key),
      };
    }
    return inferred;
  }, [formData, hitl.formSchema]);

  const fieldErrors = hitl.formValidation?.fieldErrors ?? {};
  const isSubmitting = disabled || isSubmittingLocal;

  const updateField = (field: string, value: unknown): void => {
    setFormData((previous) => {
      if (value === undefined) {
        const next = { ...previous };
        delete next[field];
        return next;
      }
      return {
        ...previous,
        [field]: value,
      };
    });
  };

  const renderField = (key: string, field: HITLFormField): JSX.Element => {
    const rawValue = formData[key];
    const value = rawValue === null || rawValue === undefined ? '' : String(rawValue);
    const hasError = Boolean(fieldErrors[key]);

    if (field.type === 'textarea') {
      return (
        <Textarea
          value={value}
          rows={3}
          onChange={(event) => updateField(key, event.target.value)}
          disabled={isSubmitting}
          placeholder={field.placeholder}
          className={cn(hasError ? 'border-red-400 focus-visible:ring-red-500' : undefined)}
        />
      );
    }

    if (field.type === 'select') {
      return (
        <select
          value={value}
          onChange={(event) => updateField(key, event.target.value)}
          disabled={isSubmitting}
          className={cn(
            'h-9 w-full rounded-lg border bg-white px-3 text-sm',
            hasError ? 'border-red-400' : 'border-gray-200',
          )}
        >
          <option value="">Sélectionner...</option>
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      );
    }

    return (
      <Input
        type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'}
        value={value}
        onChange={(event) => updateField(
          key,
          field.type === 'number'
            ? (event.target.value === '' ? undefined : event.target.valueAsNumber)
            : event.target.value,
        )}
        disabled={isSubmitting}
        placeholder={field.placeholder}
        min={field.minimum}
        max={field.maximum}
        minLength={field.minLength}
        maxLength={field.maxLength}
        pattern={field.pattern}
        error={hasError}
      />
    );
  };

  const handleSubmit = async (): Promise<void> => {
    if (isSubmitting) return;
    setIsSubmittingLocal(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmittingLocal(false);
    }
  };

  const handleCancel = async (): Promise<void> => {
    if (isSubmitting) return;
    setIsSubmittingLocal(true);
    try {
      await onCancel();
    } finally {
      setIsSubmittingLocal(false);
    }
  };

  return (
    <div className="space-y-3 rounded-xl border border-domino-cardBorder bg-domino-lilac p-3">
      {(hitl.formTitle || hitl.formDescription) && (
        <div>
          {hitl.formTitle && (
            <p className="text-xs font-semibold uppercase tracking-wide text-domino-aubergine">{hitl.formTitle}</p>
          )}
          {hitl.formDescription && (
            <p className="mt-1 text-xs text-domino-prune">{hitl.formDescription}</p>
          )}
        </div>
      )}

      <div className="space-y-3">
        {Object.entries(schema).map(([key, field]) => (
          <div key={key} className="space-y-1">
            <label className="text-xs font-medium text-gray-700">
              {field.label ?? defaultLabelFromKey(key)}
              {field.required ? <span className="ml-1 text-red-600">*</span> : null}
            </label>
            {renderField(key, field)}
            {fieldErrors[key] && (
              <p className="text-[11px] text-red-700">
                {fieldErrors[key].message}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={isSubmitting}
          onClick={() => void handleCancel()}
        >
          Annuler
        </Button>
        <Button
          size="sm"
          variant="primary"
          loading={isSubmittingLocal}
          disabled={isSubmitting}
          onClick={() => void handleSubmit()}
        >
          Valider
        </Button>
      </div>
    </div>
  );
};
